import React, { useEffect } from 'react';

function Datenschutz({ language, toggleLanguage, navigateTo }) {
  // Add a class to the body when the privacy policy page is active
  useEffect(() => {
    // Add privacy-policy class to body
    document.body.classList.add('privacy-policy');
    
    // Clean up function to remove class when component unmounts
    return () => {
      document.body.classList.remove('privacy-policy');
    };
  }, []);

  const content = {
    en: {
      title: 'Privacy Policy',
      
      // Section 1: Data Protection at a Glance
      section1: '1. Data Protection at a Glance',
      generalInfo: 'General Information',
      generalInfoText: 'The following notes provide a simple overview of what happens to your personal data when you visit this website. Personal data is any data with which you can be personally identified.',
      controller: 'Controller',
      controllerText: 'The data processing on this website is carried out by the website operator:',
      controllerName: 'Waltraud Pfister-Bläske',
      controllerBusiness: 'atelier-wpb',
      controllerAddress: 'Johann-Biersack-Straße 27b',
      controllerCity: '82340 Feldafing',
      controllerEmail: 'Email: atelier.wpb.feldafing@gmail.com',
      
      // Section 2: Hosting
      section2: '2. Hosting and Content Delivery Networks (CDN)',
      hostingTitle: 'External Hosting (Oracle Cloud)',
      hostingText: 'This website is hosted by an external service provider (Hoster). The personal data collected on this website is stored on the Hoster\'s servers. This may include, but is not limited to, IP addresses, contact requests, meta and communication data, contract data, contact details, names, website access, and other data generated via a website.',
      hostingProvider: 'Our Hoster is: Oracle Cloud Infrastructure (OCI)',
      hostingRegion: 'Region: Frankfurt, Germany',
      hostingLegal: 'The use of the Hoster is for the purpose of fulfilling contracts with our potential and existing customers (Art. 6(1)(b) GDPR) and in the interest of a secure, fast, and efficient provision of our online offer by a professional provider (Art. 6(1)(f) GDPR).',
      
      // Section 3: General Notes
      section3: '3. General Notes and Mandatory Information',
      sslTitle: 'Data Security (SSL or TLS Encryption)',
      sslText: 'For security reasons and to protect the transmission of confidential content, such as orders or inquiries that you send to us as the site operator, this site uses SSL or TLS encryption (provided by Let\'s Encrypt / Certbot). You can recognize an encrypted connection by the fact that the address line of the browser changes from "http://" to "https://" and by the lock symbol in your browser line. If SSL or TLS encryption is activated, the data you transmit to us cannot be read by third parties.',
      storageTitle: 'Storage Duration',
      storageText: 'Unless a specific storage period has been stated in this privacy policy, your personal data will remain with us until the purpose for the data processing no longer applies. If you assert a legitimate request for deletion or revoke your consent to data processing, your data will be deleted unless we have other legally permissible reasons for storing your personal data (e.g., tax or commercial retention periods); in the latter case, the deletion will take place after these reasons cease to apply.',
      
      // Section 4: Data Collection
      section4: '4. Data Collection on this Website',
      cookiesTitle: 'Cookies',
      cookiesText: 'Our website uses technically necessary cookies or local storage technologies that are required for the operation of the site (e.g., session handling). We do not use tracking or marketing cookies that would require consent. The legal basis for storing strictly necessary cookies is Art. 6(1)(f) GDPR (legitimate interest in the error-free technical provision of the website).',
      serverLogTitle: 'Server Log Files',
      serverLogText: 'The provider of the pages (Oracle Cloud) automatically collects and stores information in so-called server log files, which your browser automatically transmits to us. These are:',
      serverLogList: '• Browser type and browser version\n• Operating system used\n• Referrer URL (the previously visited page)\n• Hostname of the accessing computer\n• Time of the server request\n• IP Address',
      serverLogLegal: 'This data is not merged with other data sources. The collection of this data is based on Art. 6(1)(f) GDPR. The website operator has a legitimate interest in the technically error-free presentation and optimization of their website – for this purpose, the server log files must be recorded.',
      contactTitle: 'Contact Form and Email Contact',
      contactText: 'If you send us inquiries via the contact form, your details from the inquiry form, including the contact details you provided there, will be stored by us for the purpose of processing the inquiry and in case of follow-up questions.',
      contactGmail: 'Processing via Gmail: We use Google services (Gmail) for receiving and sending emails. Your message will be forwarded to our address atelier.wpb.feldafing@gmail.com.',
      contactRateLimit: 'Security Measure (Rate Limiting): To prevent misuse, spam, and automated attacks (DDoS), we store and process your IP address and the timestamp of your request. We use this data to limit the number of requests per IP address over time (Rate Limiting, e.g., max. 3 requests per hour). This is done on the basis of our legitimate interest in the security of our IT systems pursuant to Art. 6(1)(f) GDPR.',
      contactLegal: 'The processing of the data entered into the contact form (Name, Email, Message) takes place on the basis of Art. 6(1)(b) GDPR, provided that your inquiry is related to the fulfillment of a contract or is necessary for the implementation of pre-contractual measures. In all other cases, the processing is based on our legitimate interest in the effective processing of the inquiries addressed to us (Art. 6(1)(f) GDPR).',
      contactStorage: 'The data you enter in the contact form will remain with us until you ask us to delete it, revoke your consent to storage, or the purpose for data storage no longer applies (e.g., after your inquiry has been processed). Mandatory statutory provisions – in particular statutory retention periods – remain unaffected.',
      
      // Section 5: Your Rights
      section5: '5. Your Rights',
      rightsText: 'You have the right to receive information about the origin, recipient, and purpose of your stored personal data free of charge at any time. You also have the right to demand the correction or deletion of this data. If you have given your consent to data processing, you can revoke this consent at any time for the future. Furthermore, you have the right to request the restriction of the processing of your personal data under certain circumstances. You also have the right to lodge a complaint with the competent supervisory authority.',
      rightsContact: 'For this purpose, as well as for further questions regarding data protection, you can contact us at any time at the address given in the Legal Notice (Impressum).',
      
      backToHome: 'Back to Home'
    },
    de: {
      title: 'Datenschutzerklärung',
      
      // Section 1: Datenschutz auf einen Blick
      section1: '1. Datenschutz auf einen Blick',
      generalInfo: 'Allgemeine Hinweise',
      generalInfoText: 'Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.',
      controller: 'Verantwortliche Stelle',
      controllerText: 'Die Datenverarbeitung auf dieser Website erfolgt durch die Websitebetreiberin:',
      controllerName: 'Waltraud Pfister-Bläske',
      controllerBusiness: 'atelier-wpb',
      controllerAddress: 'Johann-Biersack-Straße 27b',
      controllerCity: '82340 Feldafing',
      controllerEmail: 'E-Mail: atelier.wpb.feldafing@gmail.com',
      
      // Section 2: Hosting
      section2: '2. Hosting und Content Delivery Networks (CDN)',
      hostingTitle: 'Externes Hosting (Oracle Cloud)',
      hostingText: 'Diese Website wird bei einem externen Dienstleister gehostet (Hoster). Die personenbezogenen Daten, die auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert. Hierbei kann es sich v. a. um IP-Adressen, Kontaktanfragen, Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Webseitenzugriffe und sonstige Daten, die über eine Website generiert werden, handeln.',
      hostingProvider: 'Unser Hoster ist: Oracle Cloud Infrastructure (OCI)',
      hostingRegion: 'Region: Frankfurt, Deutschland',
      hostingLegal: 'Die Nutzung des Hosters erfolgt zum Zwecke der Vertragserfüllung gegenüber unseren potenziellen und bestehenden Kunden (Art. 6 Abs. 1 lit. b DSGVO) und im Interesse einer sicheren, schnellen und effizienten Bereitstellung unseres Online-Angebots durch einen professionellen Anbieter (Art. 6 Abs. 1 lit. f DSGVO).',
      
      // Section 3: Allgemeine Hinweise
      section3: '3. Allgemeine Hinweise und Pflichtinformationen',
      sslTitle: 'Datensicherheit (SSL- bzw. TLS-Verschlüsselung)',
      sslText: 'Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie zum Beispiel Bestellungen oder Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung (bereitgestellt durch Let\'s Encrypt / Certbot). Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile. Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können die Daten, die Sie an uns übermitteln, nicht von Dritten mitgelesen werden.',
      storageTitle: 'Speicherdauer',
      storageText: 'Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt wurde, verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck für die Datenverarbeitung entfällt. Wenn Sie ein berechtigtes Löschersuchen geltend machen oder eine Einwilligung zur Datenverarbeitung widerrufen, werden Ihre Daten gelöscht, sofern wir keine anderen rechtlich zulässigen Gründe für die Speicherung Ihrer personenbezogenen Daten haben (z. B. steuer- oder handelsrechtliche Aufbewahrungsfristen); im letztgenannten Fall erfolgt die Löschung nach Fortfall dieser Gründe.',
      
      // Section 4: Datenerfassung
      section4: '4. Datenerfassung auf dieser Website',
      cookiesTitle: 'Cookies',
      cookiesText: 'Unsere Website verwendet technisch notwendige Cookies bzw. lokale Speichertechniken, die für den Betrieb der Seite erforderlich sind (z.B. Session-Handling). Wir verwenden keine Tracking- oder Marketing-Cookies, die eine Einwilligung erfordern würden. Rechtsgrundlage für die Speicherung unbedingt erforderlicher Cookies ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der fehlerfreien technischen Bereitstellung).',
      serverLogTitle: 'Server-Log-Dateien',
      serverLogText: 'Der Provider der Seiten (Oracle Cloud) erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:',
      serverLogList: '• Browsertyp und Browserversion\n• Verwendetes Betriebssystem\n• Referrer URL (die zuvor besuchte Seite)\n• Hostname des zugreifenden Rechners\n• Uhrzeit der Serveranfrage\n• IP-Adresse',
      serverLogLegal: 'Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes Interesse an der technisch fehlerfreien Darstellung und der Optimierung seiner Website – hierzu müssen die Server-Log-Files erfasst werden.',
      contactTitle: 'Kontaktformular und E-Mail-Kontakt',
      contactText: 'Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert.',
      contactGmail: 'Verarbeitung via Gmail: Wir nutzen für den E-Mail-Empfang und -Versand die Dienste von Google (Gmail). Ihre Nachricht wird an unsere Adresse atelier.wpb.feldafing@gmail.com weitergeleitet.',
      contactRateLimit: 'Sicherheitsmaßnahme (Rate Limiting): Zur Verhinderung von Missbrauch, Spam und automatisierten Angriffen (DDoS) speichern und verarbeiten wir Ihre IP-Adresse sowie den Zeitstempel Ihrer Anfrage. Wir nutzen diese Daten, um die Anzahl der Anfragen pro IP-Adresse zeitlich zu begrenzen (Rate Limiting, z.B. max. 3 Anfragen pro Stunde). Dies erfolgt auf Grundlage unseres berechtigten Interesses an der Sicherheit unserer IT-Systeme gemäß Art. 6 Abs. 1 lit. f DSGVO.',
      contactLegal: 'Die Verarbeitung der in das Kontaktformular eingegebenen Daten (Name, E-Mail, Nachricht) erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO).',
      contactStorage: 'Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung entfällt (z. B. nach abgeschlossener Bearbeitung Ihrer Anfrage). Zwingende gesetzliche Bestimmungen – insbesondere Aufbewahrungsfristen – bleiben unberührt.',
      
      // Section 5: Ihre Rechte
      section5: '5. Ihre Rechte',
      rightsText: 'Sie haben jederzeit das Recht auf unentgeltliche Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.',
      rightsContact: 'Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit unter der im Impressum angegebenen Adresse an uns wenden.',
      
      backToHome: 'Zurück zur Startseite'
    }
  };

  return (
    <>
      <header>
        <div className="container header-container">
          <button 
            className="logo-button" 
            onClick={() => navigateTo('home', 'work')}  // Pass 'work' as the section
          >
            {language === 'en' ? 'atelier-wpb' : 'atelier-wpb'}
          </button>
          <nav>
            <ul>
              <li>
                <a 
                  href="#work" 
                  className="nav-link" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    navigateTo('home', 'work');
                  }}
                >
                  {language === 'en' ? 'My Work' : 'Meine Arbeiten'}
                </a>
              </li>
              <li>
                <a 
                  href="#about" 
                  className="nav-link" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    navigateTo('home', 'about');
                  }}
                >
                  {language === 'en' ? 'About' : 'Über Mich'}
                </a>
              </li>
              <li>
                <a 
                  href="#contact" 
                  className="nav-link" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    navigateTo('home', 'contact');
                  }}
                >
                  {language === 'en' ? 'Contact' : 'Kontakt'}
                </a>
              </li>
            </ul>
            <button className="language-switch" onClick={toggleLanguage}>
              {language === 'en' ? 'DE' : 'EN'}
            </button>
          </nav>
        </div>
      </header>
      
      <section className="section-content">
        <div className="container">
          <h1>{content[language].title}</h1>
          <div className="legal-content">
            {/* Section 1: Data Protection at a Glance */}
            <h2>{content[language].section1}</h2>
            <h3>{content[language].generalInfo}</h3>
            <p>{content[language].generalInfoText}</p>
            <h3>{content[language].controller}</h3>
            <p>{content[language].controllerText}</p>
            <p>
              {content[language].controllerName}<br />
              {content[language].controllerBusiness}<br />
              {content[language].controllerAddress}<br />
              {content[language].controllerCity}
            </p>
            <p>{content[language].controllerEmail}</p>
            
            {/* Section 2: Hosting */}
            <h2>{content[language].section2}</h2>
            <h3>{content[language].hostingTitle}</h3>
            <p>{content[language].hostingText}</p>
            <p>{content[language].hostingProvider}<br />{content[language].hostingRegion}</p>
            <p>{content[language].hostingLegal}</p>
            
            {/* Section 3: General Notes */}
            <h2>{content[language].section3}</h2>
            <h3>{content[language].sslTitle}</h3>
            <p>{content[language].sslText}</p>
            <h3>{content[language].storageTitle}</h3>
            <p>{content[language].storageText}</p>
            
            {/* Section 4: Data Collection */}
            <h2>{content[language].section4}</h2>
            <h3>{content[language].cookiesTitle}</h3>
            <p>{content[language].cookiesText}</p>
            <h3>{content[language].serverLogTitle}</h3>
            <p>{content[language].serverLogText}</p>
            <p style={{ whiteSpace: 'pre-line' }}>{content[language].serverLogList}</p>
            <p>{content[language].serverLogLegal}</p>
            <h3>{content[language].contactTitle}</h3>
            <p>{content[language].contactText}</p>
            <p>{content[language].contactGmail}</p>
            <p>{content[language].contactRateLimit}</p>
            <p>{content[language].contactLegal}</p>
            <p>{content[language].contactStorage}</p>
            
            {/* Section 5: Your Rights */}
            <h2>{content[language].section5}</h2>
            <p>{content[language].rightsText}</p>
            <p>{content[language].rightsContact}</p>
            
            <div className="back-link">
              <button 
                className="link-button" 
                onClick={() => navigateTo('home', 'work')} // Pass 'work' as the section
              >
                {content[language].backToHome}
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* ...existing footer... */}
    </>
  );
}

export default Datenschutz;
